import React, { useState, useEffect, useMemo } from 'react';
import dayjs from 'dayjs';
import '../TopMenu/Residents.css';
import {lightBlue} from "@mui/material/colors";
import Grid from "@mui/system/Unstable_Grid";


import {
  Box,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  CircularProgress,
  Typography,
  Button,
  Stack,
  Chip
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { SERVER_URL } from '../../constants';
import PaymentDialog from '../AddsEdits/Student/PaymentDialog.js';
import { submitPaymentWithDuplicateGuard } from '../shared utils/Payments.js';
import { downloadOutstandingBalancesCsv } from './outstandingBalancesExport';
import GroupPaymentDialog from '../Booking Groups/GroupPaymentDialog.jsx';
import { createGroupPayment } from '../Booking Groups/api/finance.js';
import AddIcon from '@mui/icons-material/Add';
import IconButton from '@mui/material/IconButton';

// Sort key for the arrival date — invalid/blank dates sort last.
function arrivalSortKey(value) {
  if (!value) return Infinity;
  const d = dayjs(value);
  return d.isValid() ? d.valueOf() : Infinity;
}

// Money comes back as 32-bit floats, so summed payments carry sub-penny
// rounding error (e.g. £1294.9999659 against a £1295 booking). Round every
// money field to the penny so the figures are clean.
function roundPenny(value) {
  return Math.round((Number(value) || 0) * 100) / 100;
}


export default function OutstandingBalances({ selectedYear, onSelectStudent }) {
  const [debts, setDebts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Group-billed rows record payments against the group, not a student.
  const [groupPaymentRow, setGroupPaymentRow] = useState(null);
  const [savingGroupPayment, setSavingGroupPayment] = useState(false);

  const fetchDebts = () => {
  const token = sessionStorage.getItem('bearer');
  //setLoading(true); // optional spinner
  fetch(`${SERVER_URL}api/outstandingBalances/${selectedYear}`, {
    headers: { Authorization: `Bearer ${token}` },
  })
    .then((res) => {
      if (res.status === 204) return [];
      if (!res.ok) throw new Error(`Server responded with ${res.status}`);
      return res.json();
    })
    .then((data) => {
      const cleaned = (Array.isArray(data) ? data : [])
        .map((r) => ({
          ...r,
          totalBookingValue: roundPenny(r.totalBookingValue),
          totalPayments: roundPenny(r.totalPayments),
          outstandingBalance: roundPenny(r.outstandingBalance),
        }))
        // Drop rows that settle to £0.00 — fully-paid bookings that only
        // survive the backend's "> 0" filter as float residue (you can't owe
        // less than a penny). Genuine credits (negative balances) stay.
        .filter((r) => r.outstandingBalance !== 0);
      setDebts(cleaned);
      setLoading(false);
    })
    .catch((err) => {
      console.error('Failed to fetch outstanding balances:', err);
      setError(err.message || 'Unknown error');
      setLoading(false);
    });
};

  useEffect(() => {
    fetchDebts();
  }, [selectedYear]);

  // Auto-sort by arrival date (ascending), blanks last.
  const sortedDebts = useMemo(
    () =>
      [...debts].sort(
        (a, b) => arrivalSortKey(a.arrival) - arrivalSortKey(b.arrival)
      ),
    [debts]
  );

  const handleSaveGroupPayment = (payload) => {
    if (!groupPaymentRow) return;
    const token = sessionStorage.getItem('bearer');
    setSavingGroupPayment(true);
    createGroupPayment(groupPaymentRow.bookingGroupId, payload, token)
      .then(() => {
        setGroupPaymentRow(null);
        fetchDebts();
      })
      .catch((err) => console.error('Failed to record group payment:', err))
      .finally(() => setSavingGroupPayment(false));
  };

  if (loading) {
    return (
      <Box
        sx={{
          width: '100%',
          typography: 'body1',
          justifyContent: 'center',
          alignItems: 'center',
          display: 'flex',
          mt: 4,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          width: '100%',
          typography: 'body1',
          justifyContent: 'center',
          alignItems: 'center',
          display: 'flex',
          p: 4,
        }}
      >
        <Typography color="error">
          Failed to load outstanding balances: {error}
        </Typography>
      </Box>
    );
  }

  if (debts.length === 0) {
    return (
      <Box
        sx={{
          width: '100%',
          typography: 'body1',
          justifyContent: 'center',
          alignItems: 'center',
          display: 'flex',
          p: 4,
        }}
      >
        <Typography>No outstanding balances found for {selectedYear}.</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        typography: 'body1',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        p: 2,
      }}
    >
      <Stack
        direction="row"
        justifyContent="flex-end"
        sx={{ width: '100%', maxWidth: 1900, mb: 1 }}
      >
        <Button
          variant="outlined"
          size="small"
          startIcon={<DownloadIcon />}
          onClick={() => downloadOutstandingBalancesCsv(sortedDebts, selectedYear)}
        >
          Export CSV
        </Button>
      </Stack>
      <TableContainer
        component={Paper}
        sx={{
        margin: '0 auto',
        maxWidth: 1900,
        }}
      >
      <Grid
        sx={{
          display: 'inline',
          alignContent: 'flex-start',
          position: 'left',
          bgcolor: lightBlue[100],
          width: '100%',
          height: 0, // zero height if you only want the strip color
        }}
      />
      <Table
        sx={{
          minWidth: 800,
          color: lightBlue[200],
          margin: '0 auto',
        }}
        size="small"
        aria-label="Outstanding Balances Table"
      >
          <TableHead>
            <TableRow sx={{ bgcolor: lightBlue[100], border: 1.5 }}>
              <TableCell sx={{ border: 1.5 }} align="center"><strong>Student</strong></TableCell>
              <TableCell sx={{ border: 1.5 }} align="center"><strong>Name</strong></TableCell>
              <TableCell sx={{ border: 1.5 }} align="center"><strong>Arrival</strong></TableCell>
              <TableCell sx={{ border: 1.5 }} align="center"><strong>Departure</strong></TableCell>
              <TableCell sx={{ border: 1.5 }} align="center"><strong>Total</strong></TableCell>
              <TableCell sx={{ border: 1.5 }} align="center"><strong>Paid</strong></TableCell>
              <TableCell sx={{ border: 1.5 }} align="center"><strong>Outstanding</strong></TableCell>
              <TableCell sx={{ border: 1.5 }} align="center"><strong>Agent</strong></TableCell>
              <TableCell sx={{ border: 1.5 }} align="center"><strong>Agent Phone</strong></TableCell>
              <TableCell sx={{ border: 1.5 }} align="center"><strong>Agent Email</strong></TableCell>
              <TableCell sx={{ border: 1.5 }} align="center"><strong>Add Payment</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedDebts.map((row) => {
              const isGroup = row.rowType === 'GROUP';
              const gbp = (v) =>
                Number(v).toLocaleString('en-GB', {
                  style: 'currency',
                  currency: 'GBP',
                });
              return (
                <TableRow
                  key={isGroup ? `g-${row.bookingGroupId}` : `s-${row.studentId}`}
                >
                  <TableCell sx={{ border: 1.5, textAlign: 'center' }}>
                    {isGroup ? (
                      <Chip label="Group" size="small" color="info" />
                    ) : (
                      <Box
                        component="span"
                        onClick={() => onSelectStudent(row.studentId)}
                        sx={{
                          cursor: 'pointer',
                          color: 'blue',
                          textDecoration: 'underline',
                        }}
                      >
                        {row.studentId}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    {isGroup ? row.bookingGroupName : row.studentName}
                  </TableCell>
                  <TableCell>{row.arrival}</TableCell>
                  <TableCell>{row.departure}</TableCell>
                  <TableCell align="right">{gbp(row.totalBookingValue)}</TableCell>
                  <TableCell align="right">{gbp(row.totalPayments)}</TableCell>
                  <TableCell align="right">{gbp(row.outstandingBalance)}</TableCell>
                  <TableCell>{row.agentName}</TableCell>
                  <TableCell>{row.agentPhone}</TableCell>
                  <TableCell>{row.agentEmail}</TableCell>
                  <TableCell align="center">
                    {isGroup ? (
                      <IconButton
                        color="primary"
                        onClick={() => setGroupPaymentRow(row)}
                      >
                        <AddIcon />
                      </IconButton>
                    ) : (
                      <PaymentDialog
                        studentId={row.studentId}
                        trigger={
                          <IconButton color="primary">
                            <AddIcon />
                          </IconButton>
                        }
                        addPayment={(payment) => {
                          return submitPaymentWithDuplicateGuard(
                            row.studentId,
                            payment
                          ).then((result) => {
                            if (result.ok) {
                              fetchDebts();
                            }
                            // keep the dialog open if the user declined the duplicate
                            return result.cancelled;
                          });
                        }}
                      />
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <GroupPaymentDialog
        open={Boolean(groupPaymentRow)}
        onClose={() => setGroupPaymentRow(null)}
        onSave={handleSaveGroupPayment}
        saving={savingGroupPayment}
      />
    </Box>
  );
}